import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { Phone, Navigation, ChevronDown } from "lucide-react";
import { IMAGES, BRAND } from "../data/content";
import { track } from "../lib/tracking";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const [index, setIndex] = useState(0);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacityContent = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % IMAGES.hero.length), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex h-[100svh] min-h-[620px] items-end overflow-hidden">
      <motion.div style={{ y: yBg }} className="absolute inset-0 scale-110">
        <AnimatePresence mode="sync">
          <motion.img
            key={index}
            src={IMAGES.hero[index].src}
            alt={IMAGES.hero[index].alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: "easeInOut" }}
            className="animate-kenburns absolute inset-0 h-full w-full object-cover"
            loading={index === 0 ? "eager" : "lazy"}
            fetchpriority={index === 0 ? "high" : "auto"}
          />
        </AnimatePresence>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/45 to-obsidian/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-transparent to-obsidian/40" />

      <motion.div style={{ opacity: opacityContent }} className="container-x relative z-10 pb-28 sm:pb-32">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.9 }}
          className="eyebrow mb-6"
          data-testid="hero-eyebrow"
        >
          Premium Event Security · Andhra Pradesh & Telangana
        </motion.p>
        <h1 className="max-w-5xl font-serif text-[13vw] font-medium leading-[1.02] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.45}>Commanding Presence.</MaskedLine>
          <MaskedLine delay={0.62} className="text-gold">
            <em className="font-display italic">Effortless Hospitality.</em>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg"
          data-testid="hero-subtext"
        >
          Premium bouncers, VIP movement support and guest-focused event security for weddings,
          launches, corporate events, concerts and high-profile gatherings across Andhra Pradesh
          and Telangana.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-plan-event-btn"
            className="bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-gold-soft"
          >
            Plan Your Event Security
          </Link>
          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            onClick={() => track("whatsapp_click", { placement: "hero" })}
            className="border border-ivory/30 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-ivory transition-all duration-300 hover:border-gold hover:text-gold"
          >
            WhatsApp Our Team
          </a>
          <div className="flex items-center gap-5 pl-1 text-sm">
            <a
              href={BRAND.phonePrimaryHref}
              data-testid="hero-call-link"
              onClick={() => track("call_click", { placement: "hero" })}
              className="flex items-center gap-2 text-ivory/70 transition-colors duration-300 hover:text-gold"
            >
              <Phone size={14} className="text-gold" /> Call
            </a>
            <span className="h-4 w-px bg-ivory/20" aria-hidden="true" />
            <a
              href={BRAND.directions}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-directions-link"
              onClick={() => track("directions_click", { placement: "hero" })}
              className="flex items-center gap-2 text-ivory/70 transition-colors duration-300 hover:text-gold"
            >
              <Navigation size={14} className="text-gold" /> Directions
            </a>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-24 left-1/2 z-10 -translate-x-1/2 md:bottom-10"
        aria-hidden="true"
      >
        <div className="flex h-12 w-7 items-start justify-center rounded-full border border-ivory/25 p-1.5">
          <div className="animate-scroll-dot h-2 w-2 rounded-full bg-gold" />
        </div>
        <ChevronDown size={14} className="mx-auto mt-2 text-ivory/40" />
      </motion.div>
    </section>
  );
};
